import { defineComponent, PropType } from 'vue';
import { HasTaskMixin } from '@/mixins';
import pDebounce from 'debounce-promise';
import { QueryResult, Post, PostFilters } from '@/type';
import eventBus from '@/messaging/event-bus';
import {
  PostSaved,
  PostEscalated,
  PostResolved,
  PostTrashed,
  PostRestored,
  PostRemovedFromTimeline,
  PostMovedToTimeline,
  PostEvent,
  PostNoteAdded,
} from '@/messaging/events';
import repository from '../post-repository';
import { matches } from '../post';

type DebouncedFetch = (filters: PostFilters) => Promise<QueryResult>;

interface Data {
  task: Promise<QueryResult>|undefined,
  isLoading: boolean,
  page: number,
  total: number,
  posts: Post[],
  doFetch: DebouncedFetch
}

type FetchFn = (filters: PostFilters) => Promise<QueryResult<Post>>;

export default defineComponent({
  emits: ['total'],
  mixins: [HasTaskMixin],
  props: {
    filters: {
      type: Object as () => PostFilters,
      required: true,
    },
    persist: {
      type: Boolean,
      default: false,
    },
    fetch: {
      type: Function as PropType<FetchFn>,
      required: false,
    },
  },
  data(): Data {
    return {
      task: undefined,
      isLoading: false,
      page: 1,
      total: 0,
      posts: [],
      // Placeholder to stop Typescript complaining
      doFetch: (() => Promise.resolve({} as QueryResult)) as DebouncedFetch,
    };
  },
  computed: {
    isClientContext(): boolean {
      return Array.isArray(this.filters.clientIds) && (this.filters.clientIds.length === 1);
    },
    statuses(): number[] {
      return this.filters.statuses;
    },
    isTrashed(): boolean|undefined {
      return this.filters.isTrashed;
    },
    isOnTimeline(): boolean|undefined {
      return this.filters.isOnTimeline;
    },
    filteredPosts(): Post[] {
      return this.posts.filter(p => matches(p, this.filters));
    },
    perPage(): number {
      return this.filters.limit || 12;
    },
    limit(): number {
      return this.perPage * 2;
    },
    numVisible(): number {
      return this.persist ? this.page * this.perPage : this.perPage;
    },
    visiblePosts(): Post[] {
      return this.filteredPosts.slice(0, this.numVisible);
    },
    numPages(): number {
      return Math.ceil(this.total / this.perPage);
    },
    offset(): number {
      return this.persist ? this.filteredPosts.length : (this.page - 1) * this.perPage;
    },
  },
  watch: {
    filters: {
      handler() {
        this.resetLoad();
      },
      deep: true,
    },
    'filteredPosts.length': {
      handler(newVal: number, oldVal: number) {
        if (oldVal === 0) return;

        if ((newVal + 1) === oldVal) {
          this.total -= 1;
        } else if ((newVal - 1) === oldVal) {
          this.total += 1;
        }

        if ((newVal !== this.total) && (newVal - 1) <= this.perPage) {
          this.load();
        }
      },
    },
    page: 'load',
    total(total: number) {
      this.$emit('total', total);
    },
  },
  methods: {
    load(): void {
      const filters = {
        ...this.filters,
        offset: this.offset,
        limit: this.limit,
      };
      // @ts-ignore
      this.task = this.doFetch(filters);
      this.task.then((result: QueryResult) => {
        this.total = result.total;
        if (this.persist) {
          (result.items as Post[]).forEach(this.add);
        } else {
          this.posts = (result.items as Post[]);
        }
      });
    },
    has(id: number) {
      return (this.posts.find(p => p.id === id) !== undefined);
    },
    add(post: Post) {
      if (!this.has((post.id))) {
        this.posts.push(post);
      }
    },
    remove(post: Post) {
      const index = this.posts.findIndex(p => p.id === post.id);
      if (index >= 0) {
        this.posts.splice(index, 1);
      }
    },
    set(post: Post) {
      const index = this.posts.findIndex(p => p.id === post.id);
      if (index >= 0) {
        this.posts.splice(index, 1, post);
      }
    },
    resetLoad() {
      this.page = 1;
      this.total = 0;
      this.posts = [];
      this.load();
    },
    changePage(page: number) {
      this.page = page;
    },
    prev() {
      this.page -= 1;
    },
    next() {
      if (this.page < this.numPages && !this.isLoading) {
        this.page += 1;
      }
    },
    onPostChanged(event: PostEvent) {
      if (matches(event.post, this.filters)) {
        this.set(event.post);
      } else {
        this.remove(event.post);
      }
    },
    onPostTrashed(event: PostTrashed) {
      this.set(event.post);
    },
    onPostRestored(event: PostTrashed) {
      this.set(event.post);
    },
    onPostNoteAdded(event: PostNoteAdded) {
      const { postId } = event.note;
      const post = this.posts.find(p => p.id === postId);
      if (post) {
        post.totalNotes += 1;
      }
    },
  },
  created() {
    // IMPORTANT: Needs to be declared here instead of in "methods" so each new component instance
    // has their own debounce function instead of sharing the same one.
    this.doFetch = pDebounce(function dofetch(this: any, filters: any): Promise<QueryResult> {
      if (this.fetch) {
        return this.fetch(filters);
      }
      return repository.findAll(filters);
    }, 1000);

    this.load();

    eventBus.$on(PostSaved.eventName, this.onPostChanged);
    eventBus.$on(PostEscalated.eventName, this.onPostChanged);
    eventBus.$on(PostResolved.eventName, this.onPostChanged);
    eventBus.$on(PostTrashed.eventName, this.onPostTrashed);
    eventBus.$on(PostRestored.eventName, this.onPostRestored);
    eventBus.$on(PostMovedToTimeline.eventName, this.onPostChanged);
    eventBus.$on(PostRemovedFromTimeline.eventName, this.onPostChanged);
    eventBus.$on(PostNoteAdded.eventName, this.onPostNoteAdded);
  },
  beforeUnmount(): void {
    eventBus.$off(PostSaved.eventName, this.onPostChanged);
    eventBus.$off(PostEscalated.eventName, this.onPostChanged);
    eventBus.$off(PostResolved.eventName, this.onPostChanged);
    eventBus.$off(PostTrashed.eventName, this.onPostTrashed);
    eventBus.$off(PostRestored.eventName, this.onPostRestored);
    eventBus.$off(PostMovedToTimeline.eventName, this.onPostChanged);
    eventBus.$off(PostRemovedFromTimeline.eventName, this.onPostChanged);
    eventBus.$off(PostNoteAdded.eventName, this.onPostNoteAdded);
  },
});
